import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { BookOpen, Search, AlertTriangle, Snowflake, Shield, ChevronDown, ChevronRight, Package, FlaskConical, DollarSign, Edit2, Save, X, BarChart3, Trophy, Clock, Stethoscope, FileWarning } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { VademecumTechEdit, VademecumTechView } from "@/components/VademecumTech";


// === Helpers de completitud de información técnica ===
// Campos del Vademécum que se consideran "información técnica" relevante.
const TECH_TEXT_FIELDS = [
  "grupo_terapeutico", "subgrupo", "mecanismo_accion",
  "contraindicaciones", "advertencias", "posologia", "interacciones_criticas",
];
const TECH_LIST_FIELDS = ["especialidades", "vias_administracion", "patologias"];
const TECH_TOTAL = TECH_TEXT_FIELDS.length + TECH_LIST_FIELDS.length;

function techCompleteness(item) {
  if (!item) return { filled: 0, total: TECH_TOTAL, pct: 0 };
  let filled = 0;
  for (const f of TECH_TEXT_FIELDS) {
    const v = item[f];
    if (typeof v === "string" && v.trim()) filled++;
  }
  for (const f of TECH_LIST_FIELDS) {
    const v = item[f];
    if (Array.isArray(v) && v.length > 0) filled++;
  }
  return { filled, total: TECH_TOTAL, pct: filled / TECH_TOTAL };
}


function LoteChip({ l, daysUntil, upc }) {
  const d = daysUntil(l.fecha_vencimiento);
  const cls = d < 0 ? "bg-[#fdecea] text-[#b53a25] border-[#d99584]"
    : d <= 30 ? "bg-[#fef7ec] text-[#8a5500] border-[#f5c477]"
    : "bg-[#e9f2ee] text-[#205c45] border-[#b2d0c2]";
  const stock = parseInt(l.stock) || 0;
  const cajas = upc > 1 ? Math.floor(stock / upc) : 0;
  const sueltas = upc > 1 ? stock - cajas * upc : 0;
  return (
    <div className={`text-[11px] px-2 py-1 rounded border ${cls} font-mono`}>
      <div className="font-semibold">Lote {l.numero_lote}</div>
      <div>Vence: {l.fecha_vencimiento} {d >= 0 ? `(${d}d)` : "(VENCIDO)"}</div>
      <div>
        Stock: <strong>{stock}</strong> u.
        {l.stock_inicial && l.stock !== l.stock_inicial ? <span className="opacity-60"> / {l.stock_inicial}</span> : null}
      </div>
      {upc > 1 && (cajas > 0 || sueltas > 0) && (
        <div className="opacity-90 text-[10px]">
          {cajas > 0 && <span><strong>{cajas}</strong> caja{cajas !== 1 ? "s" : ""}</span>}
          {cajas > 0 && sueltas > 0 && <span> + </span>}
          {sueltas > 0 && <span><strong>{sueltas}</strong> suelta{sueltas !== 1 ? "s" : ""}</span>}
        </div>
      )}
      {l.ubicacion && <div className="opacity-80">📍 {l.ubicacion}</div>}
      {l.proveedor_lote && <div className="opacity-70">👤 {l.proveedor_lote}</div>}
    </div>
  );
}


function VariantCard({ v, daysUntil, canSeeCosts }) {
  const [expanded, setExpanded] = useState(false);
  const vencidos = v.lotes.filter(l => daysUntil(l.fecha_vencimiento) < 0).length;
  const criticos = v.lotes.filter(l => { const d = daysUntil(l.fecha_vencimiento); return d >= 0 && d <= 30; }).length;

  return (
    <div className="border border-[#d1dbd6] rounded-md bg-white overflow-hidden" data-testid={`variante-${v.codigo_interno}`}>
      <button type="button" onClick={() => setExpanded(!expanded)} className="w-full p-3 flex items-start gap-2 hover:bg-[#f7f9f8] text-left">
        {expanded ? <ChevronDown size={16} className="text-[#5c7368] mt-0.5" /> : <ChevronRight size={16} className="text-[#5c7368] mt-0.5" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[10px] text-[#5c7368]">{v.codigo_interno}</span>
            <span className="font-semibold text-[#15241d]">{v.nombre_comercial}</span>
            {v.marca && v.marca !== v.nombre_comercial && <span className="text-xs text-[#5c7368]">· {v.marca}</span>}
          </div>
          <div className="mt-1 flex items-center gap-2 flex-wrap text-[11px]">
            <span className="px-1.5 py-0.5 rounded bg-[#e9f2ee] text-[#205c45] border border-[#b2d0c2]"><FlaskConical size={10} className="inline mr-1" />{v.laboratorio || "—"}</span>
            {v.distribuidor && v.distribuidor !== v.laboratorio && (
              <span className="px-1.5 py-0.5 rounded bg-[#fef7ec] text-[#8a5500] border border-[#f5c477]">📦 vía {v.distribuidor}</span>
            )}
            <span className="text-[#5c7368]">{v.presentacion}</span>
            <span className="text-[#5c7368]">× {v.unidades_por_presentacion}</span>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-sm font-semibold tabular-nums">{v.stock_total}<span className="text-[10px] font-normal text-[#5c7368]"> u.</span></div>
          {v.unidades_por_presentacion > 1 && v.stock_total > 0 && (() => {
            const cajas = Math.floor(v.stock_total / v.unidades_por_presentacion);
            const sueltas = v.stock_total - cajas * v.unidades_por_presentacion;
            return (
              <div className="text-[10px] text-[#205c45] leading-tight">
                {cajas > 0 && <span><strong>{cajas}</strong> caja{cajas !== 1 ? "s" : ""}</span>}
                {cajas > 0 && sueltas > 0 && <span className="text-[#5c7368]"> + </span>}
                {sueltas > 0 && <span><strong>{sueltas}</strong> suelta{sueltas !== 1 ? "s" : ""}</span>}
              </div>
            );
          })()}
          <div className="text-[10px] text-[#8ba397]">stock total</div>
          <div className="flex gap-1 mt-0.5 justify-end">
            {vencidos > 0 && <Badge variant="destructive" className="text-[9px] h-4 px-1">{vencidos} venc</Badge>}
            {criticos > 0 && <Badge className="text-[9px] h-4 px-1 bg-[#f5c477] text-[#8a5500]">{criticos} crit</Badge>}
          </div>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-[#e9f2ee] p-3 bg-[#fbfcfb] space-y-3">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 text-[11px]">
            <InfoField label="Reg. sanitario" value={v.registro_sanitario || "—"} />
            <InfoField label="IVA" value={v.iva_exento ? "Exento" : `${v.iva_porcentaje}%`} />
            <InfoField label="PVP (público)" value={v.pvp != null ? `$${v.pvp.toFixed(4)}` : "—"} />
            <InfoField label="PVF (farmacia)" value={v.pvf != null ? `$${v.pvf.toFixed(4)}` : "—"} />
            <InfoField label="P. Techo (MSP)" value={v.precio_techo != null ? `$${(+v.precio_techo).toFixed(4)}` : "—"} highlight={v.precio_techo != null} />
            {canSeeCosts && <InfoField label="Costo unidad" value={v.costo_unidad ? `$${v.costo_unidad.toFixed(4)}` : <span className="text-[#b53a25]">sin costo</span>} />}
            {canSeeCosts && <InfoField label="Costo caja" value={v.costo_caja ? `$${v.costo_caja.toFixed(2)}` : "—"} />}
            <InfoField label="Precio unidad" value={v.precio_unidad ? `$${v.precio_unidad.toFixed(4)}` : "—"} />
            <InfoField label="Precio caja" value={v.precio_caja ? `$${v.precio_caja.toFixed(2)}` : "—"} />
            {canSeeCosts && <InfoField label="Margen" value={v.margen != null ? `${(v.margen * 100).toFixed(0)}%` : <span className="italic text-[#8ba397]">default</span>} />}
            <InfoField label="Vta unidad" value={v.permite_venta_unidad ? "Sí" : "No"} />
            <InfoField label="Vta caja" value={v.permite_venta_presentacion ? "Sí" : "No"} />
            <InfoField label="Fraccionamiento" value={v.permite_fraccionamiento ? "Sí" : "No"} />
            <InfoField label="Cadena frío" value={v.cadena_frio_efectivo ? <Badge className="bg-[#2d7aa8] text-white text-[9px] h-4 px-1"><Snowflake size={9} className="mr-0.5" />Sí{v.cadena_frio_override !== null && v.cadena_frio_override !== undefined && <span className="ml-0.5 opacity-70">(override)</span>}</Badge> : "No"} />
            <InfoField label="Controlado" value={v.controlado_efectivo ? <Badge className="bg-[#b53a25] text-white text-[9px] h-4 px-1"><Shield size={9} className="mr-0.5" />Sí{v.controlado_override !== null && v.controlado_override !== undefined && <span className="ml-0.5 opacity-70">(override)</span>}</Badge> : "No"} />
          </div>

          <div>
            <div className="text-[10px] uppercase font-semibold text-[#5c7368] mb-1.5">Lotes disponibles ({v.lotes.length})</div>
            {v.lotes.length === 0 ? (
              <div className="text-[11px] text-[#8ba397] italic">Sin lotes con stock</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1.5">
                {v.lotes.map(l => <LoteChip key={l.id} l={l} daysUntil={daysUntil} upc={v.unidades_por_presentacion} />)}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


function InfoField({ label, value, highlight = false }) {
  return (
    <div className={`rounded border p-1.5 ${highlight ? "bg-[#fef7ec] border-[#f5c477]" : "bg-white border-[#e9f2ee]"}`}>
      <div className={`text-[9px] uppercase ${highlight ? "text-[#8a5500]" : "text-[#8ba397]"}`}>{label}</div>
      <div className={`font-medium tabular-nums ${highlight ? "text-[#7a4000]" : ""}`}>{value}</div>
    </div>
  );
}


function MedicamentoRow({ item, daysUntil, onEdit, onCompare, canSeeCosts }) {
  const [expanded, setExpanded] = useState(false);
  const tech = techCompleteness(item);
  const techBadge = tech.filled === 0
    ? { cls: "bg-[#fdecea] text-[#b53a25] border-[#d99584]", txt: "Sin info técnica", icon: FileWarning }
    : tech.filled < tech.total
      ? { cls: "bg-[#fef7ec] text-[#8a5500] border-[#f5c477]", txt: `Info ${tech.filled}/${tech.total}`, icon: FileWarning }
      : { cls: "bg-[#e9f2ee] text-[#205c45] border-[#b2d0c2]", txt: "Completo", icon: Stethoscope };
  return (
    <Card className="border-[#d1dbd6] overflow-hidden" data-testid={`med-${(item.principio_activo || "sin-principio").replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="w-full p-4 hover:bg-[#f7f9f8] flex items-start gap-3">
        <button type="button" onClick={() => setExpanded(!expanded)} className="shrink-0 mt-0.5">
          {expanded ? <ChevronDown size={20} className="text-[#205c45]" /> : <ChevronRight size={20} className="text-[#205c45]" />}
        </button>
        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setExpanded(!expanded)}>
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-base font-bold text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>{item.principio_activo}</h3>
            {item.concentracion && <Badge variant="outline" className="text-xs">{item.concentracion}</Badge>}
            {item.forma_farmaceutica && <Badge variant="secondary" className="text-xs">{item.forma_farmaceutica}</Badge>}
            {item.controlado && <Badge className="bg-[#b53a25] text-white text-xs"><Shield size={10} className="mr-1" />Controlado</Badge>}
            {item.requiere_cadena_frio && <Badge className="bg-[#2d7aa8] text-white text-xs"><Snowflake size={10} className="mr-1" />Cadena frío</Badge>}
            <Badge className={`text-[10px] border ${techBadge.cls}`} data-testid={`tech-badge-${item.id}`}>
              <techBadge.icon size={10} className="mr-1" />{techBadge.txt}
            </Badge>
          </div>
          {(item.breve_descripcion || item.uso_terapeutico) && (
            <p className="text-xs text-[#5c7368] mt-1 leading-relaxed">
              {item.uso_terapeutico && <span className="font-medium">{item.uso_terapeutico}</span>}
              {item.uso_terapeutico && item.breve_descripcion && <span> · </span>}
              {item.breve_descripcion}
            </p>
          )}
          <div className="flex items-center gap-3 mt-1.5 text-[11px] text-[#4a5c53]">
            <span><Package size={10} className="inline mr-0.5" />{item.variantes_count} variantes</span>
            <span>{item.lotes_total} lotes</span>
            <span className="tabular-nums font-semibold">{item.stock_global} unidades en stock</span>
            {item.categoria && <Badge variant="outline" className="text-[9px]">{item.categoria}</Badge>}
            {item.precio_techo_base && <span className="text-[#8a5500]"><DollarSign size={10} className="inline" />Techo base ${item.precio_techo_base.toFixed(2)}</span>}
          </div>
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          {item.variantes_count > 1 && canSeeCosts && (
            <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onCompare(item.id); }} className="gap-1 h-7 text-xs" data-testid={`compare-${item.id}`}>
              <BarChart3 size={12} /> Comparar
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onEdit(item); }} className="gap-1 h-7 text-xs" data-testid={`edit-${item.id}`}>
            <Edit2 size={12} /> Editar
          </Button>
        </div>
      </div>
      {expanded && (
        <div className="border-t border-[#e9f2ee] p-3 bg-[#fbfcfb] space-y-3">
          <Tabs defaultValue="bodega">
            <TabsList>
              <TabsTrigger value="bodega" data-testid={`view-bodega-${item.id}`}><Package size={13} className="mr-1.5" />Bodega ({item.variantes_count} variantes)</TabsTrigger>
              <TabsTrigger value="tecnico" data-testid={`view-tecnico-${item.id}`}><Stethoscope size={13} className="mr-1.5" />Información técnica</TabsTrigger>
            </TabsList>
            <TabsContent value="bodega" className="space-y-2 mt-3">
              {item.variantes.map(v => <VariantCard key={v.id} v={v} daysUntil={daysUntil} canSeeCosts={canSeeCosts} />)}
            </TabsContent>
            <TabsContent value="tecnico" className="mt-3">
              <VademecumTechView item={item} />
            </TabsContent>
          </Tabs>
        </div>
      )}
    </Card>
  );
}


function EditDialog({ open, onClose, item, onSaved, opciones, productosComerciales }) {
  const [form, setForm] = useState({});
  useEffect(() => {
    if (item) setForm({
      breve_descripcion: item.breve_descripcion || "",
      uso_terapeutico: item.uso_terapeutico || "",
      categoria: item.categoria || "",
      precio_techo_base: item.precio_techo_base || "",
      controlado: item.controlado || false,
      requiere_cadena_frio: item.requiere_cadena_frio || false,
    });
  }, [item]);

  const save = async () => {
    try {
      const payload = { ...form };
      if (payload.precio_techo_base === "") payload.precio_techo_base = null;
      else payload.precio_techo_base = parseFloat(payload.precio_techo_base);
      await api.patch(`/vademecum/producto-base/${item.id}`, payload);
      toast.success(`${item.principio_activo} actualizado`);
      onSaved();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!item) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="!max-w-4xl !w-[95vw] max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit2 size={18} /> Editar Vademécum · <span className="text-[#205c45]">{item.principio_activo} {item.concentracion}</span>
          </DialogTitle>
        </DialogHeader>
        <Tabs defaultValue="basico" className="mt-2">
          <TabsList>
            <TabsTrigger value="basico" data-testid="tab-basico"><BookOpen size={13} className="mr-1.5" />Datos básicos</TabsTrigger>
            <TabsTrigger value="tecnico" data-testid="tab-tecnico"><Stethoscope size={13} className="mr-1.5" />Información técnica</TabsTrigger>
          </TabsList>
          <TabsContent value="basico" className="space-y-3 mt-3">
            <div>
              <Label className="text-xs uppercase font-semibold text-[#5c7368]">Uso terapéutico</Label>
              <Input value={form.uso_terapeutico} onChange={(e) => setForm(f => ({...f, uso_terapeutico: e.target.value}))} placeholder="Ej: Antihipertensivo, Antibiótico betalactámico" data-testid="edit-uso" />
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold text-[#5c7368]">Categoría</Label>
              <Input value={form.categoria} onChange={(e) => setForm(f => ({...f, categoria: e.target.value}))} placeholder="Ej: Cardiovascular, Antiinfeccioso, Sistema nervioso" data-testid="edit-categoria" />
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold text-[#5c7368]">Breve descripción (uso clínico)</Label>
              <Textarea value={form.breve_descripcion} onChange={(e) => setForm(f => ({...f, breve_descripcion: e.target.value}))} rows={3} placeholder="Indicaciones, dosis típica, observaciones..." data-testid="edit-desc" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs uppercase font-semibold text-[#5c7368]">Precio techo base (USD)</Label>
                <Input type="number" step="0.01" value={form.precio_techo_base} onChange={(e) => setForm(f => ({...f, precio_techo_base: e.target.value}))} placeholder="MSP/ARCSA" />
              </div>
              <div className="flex items-center gap-2 pt-5">
                <input type="checkbox" id="ctl" checked={form.controlado} onChange={(e) => setForm(f => ({...f, controlado: e.target.checked}))} />
                <label htmlFor="ctl" className="text-xs font-medium">Controlado</label>
              </div>
              <div className="flex items-center gap-2 pt-5">
                <input type="checkbox" id="cf" checked={form.requiere_cadena_frio} onChange={(e) => setForm(f => ({...f, requiere_cadena_frio: e.target.checked}))} />
                <label htmlFor="cf" className="text-xs font-medium">Cadena de frío</label>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t border-[#e9f2ee]">
              <Button variant="outline" onClick={onClose}><X size={14} /> Cancelar</Button>
              <Button onClick={save} className="bg-[#205c45]" data-testid="edit-save"><Save size={14} className="mr-1" /> Guardar datos básicos</Button>
            </div>
          </TabsContent>
          <TabsContent value="tecnico" className="mt-3">
            <VademecumTechEdit
              item={item}
              opciones={opciones}
              productosComerciales={productosComerciales}
              onSaved={onSaved}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}


function CompareDialog({ open, onClose, pbId }) {
  const { canSeeCosts: canSeeCostsFn } = useAuth();
  const canSeeCosts = canSeeCostsFn();
  const [data, setData] = useState(null);
  useEffect(() => {
    if (open && pbId) {
      api.get(`/vademecum/comparar/${pbId}`).then(r => setData(r.data)).catch(e => toast.error(formatApiError(e)));
    } else if (!open) { setData(null); }
  }, [open, pbId]);

  // Determinar el "ganador" en cada criterio
  const winners = useMemo(() => {
    if (!data?.variantes?.length) return {};
    const v = data.variantes.filter(x => x.stock_total > 0);
    if (!v.length) return {};
    return {
      mejor_margen: v.reduce((b, x) => (x.margen_real || 0) > (b.margen_real || 0) ? x : b).id,
      mas_pronto_vence: v.filter(x => x.dias_proximo_vencimiento != null).reduce((b, x) => x.dias_proximo_vencimiento < (b.dias_proximo_vencimiento || 99999) ? x : b, {}).id,
      mejor_utilidad_caja: v.reduce((b, x) => (x.utilidad_por_caja || 0) > (b.utilidad_por_caja || 0) ? x : b).id,
      mayor_stock: v.reduce((b, x) => x.stock_total > b.stock_total ? x : b).id,
    };
  }, [data]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 size={20} className="text-[#205c45]" />
            Comparar variantes — {data?.producto_base?.principio_activo} {data?.producto_base?.concentracion}
          </DialogTitle>
        </DialogHeader>
        {!data ? <div className="py-8 text-center text-[#8ba397]">Cargando...</div> : (
          <div className="space-y-3 mt-2">
            <p className="text-xs text-[#5c7368]">Decide cuál vender primero priorizando: <strong>margen real más alto</strong>, <strong>más próximo a vencer</strong> (rotar antes), <strong>mayor stock</strong>, <strong>mejor utilidad por caja</strong>.</p>
            <div className="overflow-x-auto rounded-md border border-[#d1dbd6]">
              <table className="erp-table text-xs">
                <thead>
                  <tr>
                    <th>SKU</th>
                    <th>Variante</th>
                    <th>Lab · Distrib.</th>
                    {canSeeCosts && <th className="text-right">Costo U</th>}
                    <th className="text-right">Precio U</th>
                    {canSeeCosts && <th className="text-right">Margen real</th>}
                    {canSeeCosts && <th className="text-right">Util/Caja</th>}
                    <th className="text-right">Stock</th>
                    <th className="text-right">Próx. venc.</th>
                  </tr>
                </thead>
                <tbody>
                  {data.variantes.map(v => {
                    const isMejorMargen = winners.mejor_margen === v.id;
                    const isProntoVence = winners.mas_pronto_vence === v.id;
                    const isMejorUtil = winners.mejor_utilidad_caja === v.id;
                    const isMayorStock = winners.mayor_stock === v.id;
                    return (
                      <tr key={v.id} className={v.stock_total === 0 ? "opacity-50" : ""}>
                        <td className="font-mono text-[10px]">{v.codigo_interno}</td>
                        <td>
                          <div className="font-medium">{v.nombre_comercial}</div>
                          {v.marca && <div className="text-[10px] text-[#8ba397]">{v.marca}</div>}
                        </td>
                        <td className="text-[10px]">
                          <div>{v.laboratorio}</div>
                          {v.distribuidor && v.distribuidor !== v.laboratorio && <div className="text-[#8a5500]">vía {v.distribuidor}</div>}
                        </td>
                        {canSeeCosts && <td className="text-right tabular-nums">${(v.costo_unidad || 0).toFixed(4)}</td>}
                        <td className="text-right tabular-nums">${(v.precio_unidad || 0).toFixed(4)}</td>
                        {canSeeCosts && <td className={`text-right tabular-nums ${isMejorMargen ? "bg-[#e9f2ee] font-semibold" : ""} ${(v.margen_real || 0) < 0 ? "text-[#b53a25]" : ""}`}>
                          {v.margen_real != null ? `${(v.margen_real * 100).toFixed(1)}%` : "—"}
                          {isMejorMargen && <Trophy size={11} className="inline ml-1 text-[#b07d2a]" />}
                          {(v.margen_real || 0) < 0 && <AlertTriangle size={11} className="inline ml-1" title="Margen negativo (precio < costo)" />}
                        </td>}
                        {canSeeCosts && <td className={`text-right tabular-nums ${isMejorUtil ? "bg-[#e9f2ee] font-semibold" : ""}`}>
                          ${(v.utilidad_por_caja || 0).toFixed(2)}
                          {isMejorUtil && <DollarSign size={11} className="inline ml-1 text-[#205c45]" />}
                        </td>}
                        <td className={`text-right tabular-nums ${isMayorStock ? "bg-[#eaf4fb] font-semibold" : ""}`}>
                          {v.stock_total}
                          {isMayorStock && <Package size={11} className="inline ml-1 text-[#2d7aa8]" />}
                        </td>
                        <td className={`text-right text-[10px] ${isProntoVence ? "bg-[#fef7ec] font-semibold" : ""}`}>
                          {v.proximo_vencimiento ? (
                            <>
                              {v.proximo_vencimiento}
                              <div className={v.dias_proximo_vencimiento < 30 ? "text-[#b53a25]" : "text-[#5c7368]"}>
                                {v.dias_proximo_vencimiento}d
                              </div>
                              {isProntoVence && <Clock size={10} className="inline text-[#b53a25]" />}
                            </>
                          ) : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="text-[11px] text-[#5c7368] flex flex-wrap gap-3 pt-2 border-t border-[#e9f2ee]">
              <span className="flex items-center gap-1"><Trophy size={11} className="text-[#b07d2a]" /> mejor margen</span>
              <span className="flex items-center gap-1"><DollarSign size={11} className="text-[#205c45]" /> mejor utilidad/caja</span>
              <span className="flex items-center gap-1"><Package size={11} className="text-[#2d7aa8]" /> mayor stock</span>
              <span className="flex items-center gap-1"><Clock size={11} className="text-[#b53a25]" /> rotar primero (más próximo a vencer)</span>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


export default function Vademecum() {
  const { canSeeCosts: canSeeCostsFn } = useAuth();
  const canSeeCosts = canSeeCostsFn();
  const [data, setData] = useState([]);
  const [q, setQ] = useState("");
  const [controlado, setControlado] = useState(null); // null|true|false
  const [cadenaFrio, setCadenaFrio] = useState(null);
  const [tecnicaFiltro, setTecnicaFiltro] = useState(null); // null | "sin" | "incompleta"
  const [categoriaFilter, setCategoriaFilter] = useState("");
  const [grupoFilter, setGrupoFilter] = useState("");
  const [subgrupoFilter, setSubgrupoFilter] = useState("");
  const [opciones, setOpciones] = useState({ categorias: [], grupos_terapeuticos: [], subgrupos: [], especialidades: [], vias_administracion: [], patologias: [] });
  const [productosComerciales, setProductosComerciales] = useState([]);
  const [groupByCategoria, setGroupByCategoria] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);  // item para editar
  const [comparing, setComparing] = useState(null);  // pb_id a comparar

  // Compat con código anterior
  const categorias = opciones.categorias;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (controlado !== null) params.controlado = controlado;
      if (cadenaFrio !== null) params.cadena_frio = cadenaFrio;
      if (categoriaFilter) params.categoria = categoriaFilter;
      if (grupoFilter) params.grupo_terapeutico = grupoFilter;
      if (subgrupoFilter) params.subgrupo = subgrupoFilter;
      const { data } = await api.get("/vademecum", { params });
      setData(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [q, controlado, cadenaFrio, categoriaFilter, grupoFilter, subgrupoFilter]);

  // Recargar opciones (autocomplete + filtros)
  const loadOpciones = async () => {
    try { const r = await api.get("/vademecum/categorias"); setOpciones(r.data || {}); }
    catch (err) { console.warn("opciones load:", err); }
  };

  useEffect(() => {
    loadOpciones();
    api.get("/masters/productos-comerciales").then(r => setProductosComerciales(r.data || [])).catch(() => {});
  }, []);

  // Carga inicial — cambios de filtros se piden manualmente vía botón "Buscar"
  useEffect(() => { load(); /* solo carga inicial */ /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const daysUntil = (d) => {
    if (!d) return 999;
    const t = new Date(d);
    const now = new Date();
    return Math.floor((t - now) / (24 * 3600 * 1000));
  };

  const stats = useMemo(() => ({
    medicamentos: data.length,
    variantes: data.reduce((s, d) => s + d.variantes_count, 0),
    lotes: data.reduce((s, d) => s + d.lotes_total, 0),
    stock: data.reduce((s, d) => s + d.stock_global, 0),
    controlados: data.filter(d => d.controlado).length,
    cadena_frio: data.filter(d => d.requiere_cadena_frio).length,
    sin_info: data.filter(d => techCompleteness(d).filled === 0).length,
    info_incompleta: data.filter(d => {
      const c = techCompleteness(d);
      return c.filled > 0 && c.filled < c.total;
    }).length,
  }), [data]);

  // Aplicar filtro de información técnica en cliente (los demás filtros son backend)
  const dataFiltrada = useMemo(() => {
    if (!tecnicaFiltro) return data;
    return data.filter(d => {
      const c = techCompleteness(d);
      if (tecnicaFiltro === "sin") return c.filled === 0;
      if (tecnicaFiltro === "incompleta") return c.filled < c.total;
      return true;
    });
  }, [data, tecnicaFiltro]);

  return (
    <div className="space-y-5" data-testid="vademecum-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Maestros · Vademécum</div>
          <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>
            <BookOpen className="inline mr-2 mb-1" size={28} />
            Vademécum
          </h1>
          <p className="text-sm text-[#4a5c53] mt-1">Consulta detallada por principio activo con sus variantes comerciales, lotes y datos regulatorios.</p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-3 md:grid-cols-7 gap-3">
        <Kpi label="Medicamentos" value={stats.medicamentos} />
        <Kpi label="Variantes" value={stats.variantes} />
        <Kpi label="Lotes" value={stats.lotes} />
        <Kpi label="Stock total" value={stats.stock.toLocaleString()} />
        <Kpi label="Controlados" value={stats.controlados} tone="danger" />
        <Kpi label="Cadena frío" value={stats.cadena_frio} tone="info" />
        <Kpi label="Sin info técnica" value={stats.sin_info} tone={stats.sin_info > 0 ? "danger" : "default"} />
      </div>

      {/* Filtros */}
      <Card className="p-3 border-[#d1dbd6]">
        <div className="flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[260px] relative">
            <Search size={14} className="absolute left-2.5 top-3 text-[#8ba397]" />
            <Input
              placeholder="Buscar principio activo, uso, marca, SKU..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") load(); }}
              className="pl-8 h-9"
              data-testid="search-input"
            />
          </div>
          <select
            value={categoriaFilter}
            onChange={(e) => setCategoriaFilter(e.target.value)}
            className="h-9 px-2 rounded-md border border-[#d1dbd6] text-sm bg-white min-w-[160px] max-w-[200px]"
            data-testid="categoria-filter"
          >
            <option value="">Categoría: todas</option>
            {categorias.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select
            value={grupoFilter}
            onChange={(e) => setGrupoFilter(e.target.value)}
            className="h-9 px-2 rounded-md border border-[#d1dbd6] text-sm bg-white min-w-[160px] max-w-[220px]"
            data-testid="grupo-filter"
          >
            <option value="">Grupo terapéutico: todos</option>
            {(opciones.grupos_terapeuticos || []).map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select
            value={subgrupoFilter}
            onChange={(e) => setSubgrupoFilter(e.target.value)}
            className="h-9 px-2 rounded-md border border-[#d1dbd6] text-sm bg-white min-w-[160px] max-w-[220px]"
            data-testid="subgrupo-filter"
          >
            <option value="">Subgrupo: todos</option>
            {(opciones.subgrupos || []).map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <Button variant={groupByCategoria ? "default" : "outline"} size="sm" onClick={() => setGroupByCategoria(g => !g)} className="gap-1" data-testid="toggle-group-cat">
            <BookOpen size={14} /> Agrupar por categoría
          </Button>
          <Button variant={controlado === true ? "default" : "outline"} size="sm" onClick={() => setControlado(controlado === true ? null : true)} className="gap-1">
            <Shield size={14} /> Controlados
          </Button>
          <Button variant={cadenaFrio === true ? "default" : "outline"} size="sm" onClick={() => setCadenaFrio(cadenaFrio === true ? null : true)} className="gap-1">
            <Snowflake size={14} /> Cadena frío
          </Button>
          <Button
            variant={tecnicaFiltro === "sin" ? "default" : "outline"}
            size="sm"
            onClick={() => setTecnicaFiltro(tecnicaFiltro === "sin" ? null : "sin")}
            className={`gap-1 ${tecnicaFiltro === "sin" ? "bg-[#b53a25] hover:bg-[#9a2f1f]" : ""}`}
            title="Mostrar solo medicamentos sin ningún dato técnico cargado"
            data-testid="filter-sin-info-btn"
          >
            <FileWarning size={14} /> Sin info técnica
            {stats.sin_info > 0 && <Badge className="ml-1 bg-white text-[#b53a25] text-[10px] px-1.5 py-0">{stats.sin_info}</Badge>}
          </Button>
          <Button
            variant={tecnicaFiltro === "incompleta" ? "default" : "outline"}
            size="sm"
            onClick={() => setTecnicaFiltro(tecnicaFiltro === "incompleta" ? null : "incompleta")}
            className={`gap-1 ${tecnicaFiltro === "incompleta" ? "bg-[#8a5500] hover:bg-[#704600]" : ""}`}
            title="Mostrar medicamentos con información técnica incompleta (faltan campos)"
            data-testid="filter-incompleta-btn"
          >
            <FileWarning size={14} /> Incompleta
            {(stats.sin_info + stats.info_incompleta) > 0 && <Badge className="ml-1 bg-white text-[#8a5500] text-[10px] px-1.5 py-0">{stats.sin_info + stats.info_incompleta}</Badge>}
          </Button>
          <Button onClick={load} size="sm" className="bg-[#205c45]" data-testid="search-btn">Buscar</Button>
        </div>
      </Card>

      {/* Resultados */}
      {loading ? (
        <div className="text-center py-8 text-[#8ba397]">Cargando...</div>
      ) : dataFiltrada.length === 0 ? (
        <div className="text-center py-12 text-[#8ba397]">
          <AlertTriangle size={24} className="mx-auto mb-2 opacity-50" />
          {tecnicaFiltro === "sin" ? "🎉 Todos los medicamentos tienen al menos algún dato técnico cargado." :
           tecnicaFiltro === "incompleta" ? "🎉 Todos los medicamentos tienen información técnica completa." :
           "Sin resultados"}
        </div>
      ) : groupByCategoria ? (
        <div className="space-y-6">
          {Object.entries(dataFiltrada.reduce((acc, item) => {
            const k = item.categoria || "Sin categoría";
            (acc[k] = acc[k] || []).push(item);
            return acc;
          }, {})).sort((a, b) => a[0].localeCompare(b[0])).map(([cat, items]) => (
            <div key={cat} data-testid={`cat-group-${cat.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="flex items-center gap-2 mb-2 sticky top-0 bg-[#fbfcfb] py-2 z-10 border-b border-[#d1dbd6]">
                <BookOpen size={16} className="text-[#205c45]" />
                <h2 className="text-base font-semibold text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>{cat}</h2>
                <Badge variant="outline" className="text-[10px]">{items.length} medicamento{items.length !== 1 ? "s" : ""}</Badge>
              </div>
              <div className="space-y-2">
                {items.map(item => (
                  <MedicamentoRow
                    key={item.id}
                    item={item}
                    daysUntil={daysUntil}
                    onEdit={(m) => setEditing(m)}
                    onCompare={(pbId) => setComparing(pbId)}
                    canSeeCosts={canSeeCosts}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {dataFiltrada.map(item => (
            <MedicamentoRow
              key={item.id}
              item={item}
              daysUntil={daysUntil}
              onEdit={(m) => setEditing(m)}
              onCompare={(pbId) => setComparing(pbId)}
              canSeeCosts={canSeeCosts}
            />
          ))}
        </div>
      )}

      <EditDialog
        open={!!editing}
        onClose={() => setEditing(null)}
        item={editing}
        onSaved={() => { load(); loadOpciones(); }}
        opciones={opciones}
        productosComerciales={productosComerciales}
      />
      <CompareDialog open={!!comparing} onClose={() => setComparing(null)} pbId={comparing} />
    </div>
  );
}


function Kpi({ label, value, tone = "default" }) {
  const tones = {
    default: "bg-white border-[#d1dbd6]",
    danger: "bg-[#fdecea] border-[#d99584]",
    info: "bg-[#eaf4fb] border-[#9cc6e3]",
  };
  return (
    <div className={`rounded-md border p-2.5 ${tones[tone]}`}>
      <div className="text-[10px] uppercase tracking-[0.1em] font-semibold text-[#5c7368]">{label}</div>
      <div className="text-xl font-bold tabular-nums mt-1" style={{ fontFamily: "'Manrope', sans-serif" }}>{value}</div>
    </div>
  );
}
